import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { addTransaction } from "../store/slices/transactionSlice";
import { useNavigate } from "react-router-dom";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import TransactionDatePicker from "./component/TransactionDatePicker";
import Modal from "react-modal";
import {
  ChevronDown,
  ChevronUp,
  AlertCircle,
  DollarSign,
  Calendar,
  CreditCard,
  FileText,
  Zap,
  Gift,
} from "lucide-react";
import TransactionModal from "./component/TransactionModal";
Modal.setAppElement("#root");

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.75)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  backdropFilter: "blur(8px)",
  padding: "1rem",
};

const modalStyle = {
  position: "relative",
  background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
  borderRadius: "16px",
  padding: "24px",
  width: "100%",
  maxWidth: "800px",
  maxHeight: "90vh",
  overflow: "auto",
  boxShadow:
    "0 20px 40px rgba(0, 0, 0, 0.4), 0 0 100px rgba(78, 222, 198, 0.1)",
  zIndex: 1001,
  border: "1px solid rgba(78, 222, 198, 0.1)",
};

const TransactionCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { error } = useSelector((state) => state.transactions);
  const { societies } = useSelector((state) => state.societies);
  const { activeUser } = useSelector((state) => state.auth);
  const [customTransactionTypes, setCustomTransactionTypes] = useState([]);
  const { activeSociety, theme, gradient } = useSidebarContext();
  const [formKey, setFormKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSocieties, setSelectedSocieties] = useState([]);
  const [animateForm, setAnimateForm] = useState(false);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [activeSociety?._id]);

  useEffect(() => {
    // Trigger animation after component mounts
    setAnimateForm(true);
  }, []);

  const TransactionValidationSchema = Yup.object().shape({
    amount: Yup.object()
      .required("رقم درج کریں")
      .test(
        "amount-keys-test",
        "رقم کے تمام خانے پُر کریں",
        (value) =>
          value &&
          Object.values(value).length > 0 &&
          Object.values(value).every(
            (amount) =>
              amount !== undefined &&
              amount !== null &&
              !isNaN(parseFloat(amount)) &&
              parseFloat(amount) > 0 &&
              parseFloat(amount) <= 100000000
          )
      )
      .test(
        "amount-max-test",
        "زیادہ سے زیادہ رقم 100,000,000 ہو سکتی ہے",
        (value) =>
          value &&
          Object.values(value).every(
            (amount) => parseFloat(amount) <= 100000000
          )
      ),
    transaction_type: Yup.string().required("ٹرانزیکشن کی قسم منتخب کریں"),
    transaction_direction: Yup.string().required("ٹرانزیکشن کی سمت منتخب کریں"),
    transaction_type_custom: Yup.string().when("transaction_type", {
      is: "other",
      then: (schema) => schema.required("کسٹم ٹرانزیکشن کی قسم درج کریں"),
      otherwise: (schema) => schema.notRequired(),
    }),
    description: Yup.string().max(500, "تفصیل 500 حروف سے زیادہ نہیں ہو سکتی"),
    payment_method: Yup.string().required("ادائیگی کا طریقہ درج کریں"),
  });

  // Enhanced Input and Label Classes
  const inputClasses = `mt-1 block w-full px-4 py-3 ${theme.tailwind.bg15} backdrop-blur-sm border border-slate-700/50 rounded-xl focus:ring-2 ${theme.tailwind.focusRing4} ${theme.tailwind.focus5} text-slate-100 placeholder-slate-400 transition-all duration-300 ${theme.tailwind.hoverB3} shadow-lg ${theme.tailwind.shadow2} outline-none`;

  const labelClasses = `block text-sm font-medium ${theme.tailwind.text16} mb-2 tracking-wide flex items-center space-x-2`;

  const errorClasses = "text-rose-500 text-xs mt-1 font-medium";

  // Form section styles
  const formSectionClass = `${theme.tailwind.bg27} backdrop-blur-md rounded-2xl p-6 border ${theme.tailwind.border3} shadow-xl transition-all duration-500 ${theme.tailwind.shadowhover5} relative overflow-hidden`;

  if (!activeSociety) {
    return (
      <div
        className={`min-h-screen ${gradient} flex items-center justify-center`}
      >
        <div
          className={`text-center ${theme.tailwind.bg14}/30 backdrop-blur-lg p-10 rounded-2xl border ${theme.tailwind.border3} shadow-2xl transform transition-all duration-500 hover:scale-105`}
        >
          <div className="mb-6 flex justify-center">
            <div className="w-16 h-16 rounded-full bg-rose-500/20 flex items-center justify-center border border-rose-500/30 shadow-lg shadow-rose-500/10">
              <AlertCircle className="w-8 h-8 text-rose-400" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-amber-400">
            No Society Selected
          </h2>
          <p className="text-slate-400">
            Please select a society to create transactions
          </p>
          <div className="mt-8">
            <button
              onClick={() => navigate("/")}
              className={`px-6 py-2 ${theme.tailwind.bgr12} text-white rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 ${theme.tailwind.shadowhover4}`}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSocieties([]);
  };

  const handleModalSubmit = async (values) => {
    try {
      // Ensure every society has an amount
      const formattedAmounts = {};
      values.societies.forEach((societyId) => {
        const amount = values.amounts[societyId];
        if (!amount || isNaN(amount)) {
          throw new Error(
            `Amount is missing or invalid for society ID: ${societyId}`
          );
        }
        formattedAmounts[societyId] = Number(amount);
      });

      const payload = {
        societies: values.societies,
        amount: formattedAmounts,
        transaction_type: values.transaction_type,
        transaction_direction: values.transaction_direction,
        payment_method: values.payment_method,
        transaction_date: values.transaction_date
          ? format(new Date(values.transaction_date), "yyyy-MM-dd")
          : null,
        description: values.description || "",
        user_id: activeUser.id,
      };

      const resultAction = await dispatch(addTransaction(payload));

      if (addTransaction.fulfilled.match(resultAction)) {
        enqueueSnackbar("Transaction successfully created!", {
          variant: "success",
        });
        closeModal();
        navigate("/create/transaction");
      } else {
        throw new Error(resultAction.error.message);
      }
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to create transaction", {
        variant: "error",
      });
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 transition-all duration-700">
      {/* Animated background effects */}
      <div className="fixed inset-0 overflow-hidden -z-10">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-emerald-500/5 rounded-full filter blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-blue-500/5 rounded-full filter blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-purple-500/5 rounded-full filter blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>
      </div>

      <div
        className={`max-w-5xl mx-auto transition-all duration-1000 ${
          animateForm
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div
          className={`relative ${gradient} backdrop-blur-xl rounded-3xl shadow-2xl p-8 border ${theme.tailwind.border2} overflow-hidden`}
        >
          {/* Animated border gradient */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden">
            <div
              className={`absolute top-0 left-0 right-0 h-px ${theme.tailwind.bgr8}`}
            ></div>
            <div
              className={`absolute bottom-0 left-0 right-0 h-px ${theme.tailwind.bgr11}`}
            ></div>
            <div
              className={`absolute left-0 top-0 bottom-0 w-px ${theme.tailwind.BgBottom1}`}
            ></div>
            <div
              className={`absolute right-0 top-0 bottom-0 w-px ${theme.tailwind.BgBottom2}`}
            ></div>
          </div>

          {/* Decorative elements */}
          <div
            className={`absolute -left-24 -top-24 w-96 h-96 ${theme.tailwind.bg42} rounded-full blur-3xl`}
          ></div>
          <div
            className={`absolute -right-24 -top-24 w-96 h-96 ${theme.tailwind.bg44} rounded-full blur-3xl`}
          ></div>
          <div
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-64 ${theme.tailwind.bgr9} blur-3xl transform rotate-12`}
          ></div>

          <div className="relative">
            <div className="text-center space-y-8 py-4">
              {/* Animated badge */}
              <div
                className={`inline-flex items-center px-4 py-2 rounded-full ${theme.tailwind.bgr10} border ${theme.tailwind.border20} backdrop-blur-sm mb-4 transition-all duration-500 ${theme.tailwind.groupHoverG4} shadow-lg ${theme.tailwind.shadowhover4} transform hover:scale-105`}
              >
                <span
                  className={`animate-pulse w-2 h-2 rounded-full ${theme.tailwind.bg43} mr-2`}
                ></span>
                <span
                  className={`${theme.tailwind.text16} text-sm font-medium`}
                >
                  Transaction Management
                </span>
              </div>

              {/* Main heading with animation */}
              <div className="space-y-6">
                <h1 className="text-4xl font-extrabold">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-300">
                    Create Society Transaction
                  </span>
                </h1>

                {/* Society name card with animation */}
                <div className="transform transition-all duration-500 hover:scale-105 mx-auto max-w-md">
                  <div className="relative group cursor-pointer">
                    <div
                      className={`absolute -inset-1 rounded-xl ${theme.tailwind.bgr14} opacity-30 blur group-hover:opacity-40 transition duration-300 group-hover:duration-200 animate-gradient-xy`}
                    ></div>
                    <div
                      className={`relative px-8 py-5 ${theme.tailwind.bg45} ring-1 ${theme.tailwind.ringColor3} rounded-xl`}
                    >
                      <h2
                        className={`text-3xl font-bold text-transparent bg-clip-text ${theme.tailwind.bgr13}`}
                      >
                        {activeSociety.name}
                      </h2>
                      <div className="flex items-center justify-center space-x-2 text-slate-400 mt-2 group">
                        <svg
                          className={`w-4 h-4 transform transition-transform duration-300 group-hover:scale-110 ${theme.tailwind.text16}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-lg transition-colors duration-300 text-slate-300">
                          {activeSociety.location}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Multi-society transaction button */}
              <div className="relative py-8">
                <div className="absolute inset-0 flex items-center">
                  <div
                    className={`w-full border-t ${theme.tailwind.border2}`}
                  ></div>
                </div>

                <div className="relative flex justify-center">
                  <div className={`px-4 ${theme.tailwind.bg6}`}>
                    <button
                      onClick={openModal}
                      className={`flex items-center space-x-2 px-6 py-3 ${theme.tailwind.bgr12} text-white rounded-xl shadow-lg ${theme.tailwind.shadowing1} transform transition-all duration-300 hover:scale-105 ${theme.tailwind.shadowhover4}`}
                    >
                      <Zap className="w-5 h-5" />
                      <span>Create Multi-Society Transaction</span>
                    </button>
                    <TransactionModal
                      isOpen={isModalOpen}
                      closeModal={closeModal}
                      societies={societies}
                      onSubmit={handleModalSubmit}
                      modalStyle={modalStyle}
                      modalOverlayStyle={modalOverlayStyle}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error message with enhanced styling */}
          {error && (
            <div className="my-6 p-5 bg-rose-900/20 backdrop-blur-sm border border-rose-500/30 rounded-xl relative overflow-hidden group transition-all duration-300 hover:bg-rose-900/30 shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex items-center space-x-3">
                <AlertCircle className="w-6 h-6 text-rose-400 animate-pulse" />
                <span className="text-rose-300 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Enhanced form styling */}
          <Formik
            key={formKey}
            initialValues={{
              amount: { [activeSociety._id]: "" },
              transaction_type: "",
              transaction_type_custom: "",
              transaction_direction: "",
              payment_method: "",
              description: "",
              transaction_date: null,
            }}
            validationSchema={TransactionValidationSchema}
            onSubmit={async (values, { setFieldValue, setSubmitting }) => {
              try {
                const formattedDate = values.transaction_date
                  ? format(new Date(values.transaction_date), "yyyy-MM-dd")
                  : null;

                const finalTransactionType =
                  values.transaction_type === "other"
                    ? values.transaction_type_custom
                    : values.transaction_type;

                const updatedFormData = {
                  ...values,
                  user_id: activeUser.id,
                  transaction_type: finalTransactionType,
                  societies: [activeSociety._id],
                  transaction_date: formattedDate,
                  amount: {
                    [activeSociety._id]: parseFloat(
                      values.amount[activeSociety._id]
                    ),
                  },
                };

                const resultAction = await dispatch(
                  addTransaction(updatedFormData)
                );

                if (addTransaction.fulfilled.match(resultAction)) {
                  enqueueSnackbar("Transaction successfully added!", {
                    variant: "success",
                  });
                  setFieldValue("amount", { [activeSociety._id]: "" });
                  setFieldValue("description", "");
                  navigate("/create/transaction");
                } else {
                  enqueueSnackbar("Failed to add transaction", {
                    variant: "error",
                  });
                }
              } catch (err) {
                console.error("Error creating transaction:", err);
                enqueueSnackbar("An error occurred", { variant: "error" });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting, errors, touched }) => (
              <Form className="space-y-8">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  {/* Amount Field with Icon */}
                  <div className={formSectionClass}>
                    <label htmlFor="amount" className={labelClasses}>
                      <DollarSign
                        className={`w-4 h-4 ${theme.tailwind.text16}`}
                      />
                      <span>Amount</span>
                    </label>
                    <Field name={`amount.${activeSociety._id}`}>
                      {({ field, form }) => (
                        <div className="relative">
                          <input
                            type="text"
                            id={`amount-${activeSociety._id}`}
                            {...field}
                            className={`${inputClasses} pl-12`}
                            placeholder="Enter amount"
                            value={
                              field.value
                                ? Number(
                                    field.value.toString().replace(/,/g, "")
                                  ).toLocaleString()
                                : ""
                            }
                            onChange={(event) => {
                              const rawValue = event.target.value.replace(
                                /,/g,
                                ""
                              );
                              if (!isNaN(rawValue) || rawValue === "") {
                                const updatedAmount = {
                                  ...form.values.amount,
                                  [activeSociety._id]: rawValue,
                                };
                                form.setFieldValue("amount", updatedAmount);
                              }
                            }}
                          />
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <span className={`${theme.tailwind.text16}`}>
                              $
                            </span>
                          </div>
                        </div>
                      )}
                    </Field>
                    <ErrorMessage
                      name="amount"
                      component="div"
                      className={errorClasses}
                    />
                  </div>

                  {/* Transaction Type with enhanced select */}
                  <div className={formSectionClass}>
                    <label htmlFor="transaction_type" className={labelClasses}>
                      <Gift className={`w-4 h-4 ${theme.tailwind.text16}`} />
                      <span>Transaction Type</span>
                    </label>
                    <div className="relative">
                      <Field
                        as="select"
                        id="transaction_type"
                        name="transaction_type"
                        className={`${inputClasses} cursor-pointer appearance-none`}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFieldValue("transaction_type", value);
                          if (
                            value !== "other" &&
                            value !== "" &&
                            !customTransactionTypes.includes(value)
                          ) {
                            setCustomTransactionTypes([
                              ...customTransactionTypes,
                              value,
                            ]);
                          }
                        }}
                      >
                        <option value="">Select Type</option>
                        <option
                          value="Full Payment"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Full Payment
                        </option>
                        <option
                          value="Partial Payment"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Partial Payment
                        </option>
                        <option
                          value="Installment Payment"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Installment Payment
                        </option>
                        <option
                          value="Salary Payment"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Salary Payment
                        </option>
                        <option
                          value="Expense Payment"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Expense Payment
                        </option>
                        <option
                          value="Resell Payment"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Resell Payment
                        </option>
                        <option
                          value="other"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Other
                        </option>
                        {customTransactionTypes.map((type, index) => (
                          <option
                            key={index}
                            value={type}
                            className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                          >
                            {type}
                          </option>
                        ))}
                      </Field>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ChevronDown
                          className={`h-5 w-5 ${theme.tailwind.text16}`}
                        />
                      </div>
                    </div>
                    <ErrorMessage
                      name="transaction_type"
                      component="div"
                      className={errorClasses}
                    />
                    {values.transaction_type === "other" && (
                      <div className="mt-2 transition-all duration-300">
                        <Field
                          type="text"
                          id="transaction_type_custom"
                          name="transaction_type_custom"
                          className={inputClasses}
                          placeholder="Enter custom transaction type"
                          onChange={(e) => {
                            setFieldValue(
                              "transaction_type_custom",
                              e.target.value
                            );
                            if (
                              !customTransactionTypes.includes(e.target.value)
                            ) {
                              setCustomTransactionTypes([
                                ...customTransactionTypes,
                                e.target.value,
                              ]);
                            }
                          }}
                        />
                        <ErrorMessage
                          name="transaction_type_custom"
                          component="div"
                          className={errorClasses}
                        />
                      </div>
                    )}
                  </div>

                  {/* Transaction Direction */}
                  <div className={formSectionClass}>
                    <label
                      htmlFor="transaction_direction"
                      className={labelClasses}
                    >
                      <ChevronUp
                        className={`w-4 h-4 ${theme.tailwind.text16}`}
                      />
                      <span>Transaction Direction</span>
                    </label>
                    <div className="relative">
                      <Field
                        as="select"
                        id="transaction_direction"
                        name="transaction_direction"
                        className={`${inputClasses} cursor-pointer appearance-none`}
                      >
                        <option value="">Select Direction</option>
                        <option
                          value="Income"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Income
                        </option>
                        <option
                          value="Expense"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Expense
                        </option>
                      </Field>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ChevronDown
                          className={`h-5 w-5 ${theme.tailwind.text16}`}
                        />
                      </div>
                    </div>
                    <ErrorMessage
                      name="transaction_direction"
                      component="div"
                      className={errorClasses}
                    />
                  </div>

                  {/* Payment Method */}
                  <div className={formSectionClass}>
                    <label htmlFor="payment_method" className={labelClasses}>
                      <CreditCard
                        className={`w-4 h-4 ${theme.tailwind.text16}`}
                      />
                      <span>Payment Method</span>
                    </label>
                    <div className="relative">
                      <Field
                        as="select"
                        id="payment_method"
                        name="payment_method"
                        className={`${inputClasses} cursor-pointer appearance-none`}
                      >
                        <option value="">Select Payment Method</option>
                        <option
                          value="Bank Transfer"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Bank Transfer
                        </option>
                        <option
                          value="Cash Transfer"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Cash Transfer
                        </option>
                        <option
                          value="JazzCash"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          JazzCash
                        </option>
                        <option
                          value="Easypaisa"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Easypaisa
                        </option>
                        <option
                          value="UPaisa"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          UPaisa
                        </option>
                        <option
                          value="SadaPay"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          SadaPay
                        </option>
                        <option
                          value="NayaPay"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          NayaPay
                        </option>
                        <option
                          value="PayPak"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          PayPak
                        </option>
                        <option
                          value="Other"
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          Other
                        </option>
                      </Field>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ChevronDown
                          className={`h-5 w-5 ${theme.tailwind.text16}`}
                        />
                      </div>
                    </div>
                    <ErrorMessage
                      name="payment_method"
                      component="div"
                      className={errorClasses}
                    />
                  </div>

                  {/* Date Picker */}
                  <div
                    className={`${theme.tailwind.bg14}/20 backdrop-blur-md rounded-2xl p-6 border ${theme.tailwind.border3} shadow-xl transition-all duration-500 ${theme.tailwind.shadowhover5} relative z-10`}
                  >
                    <label htmlFor="transaction_date" className={labelClasses}>
                      <Calendar
                        className={`w-4 h-4 ${theme.tailwind.text16}`}
                      />
                      <span>Date</span>
                    </label>
                    <div className="w-full">
                      <Field name="transaction_date">
                        {({ field, form }) => (
                          <TransactionDatePicker field={field} form={form} />
                        )}
                      </Field>
                    </div>
                    <ErrorMessage
                      name="transaction_date"
                      component="div"
                      className={errorClasses}
                    />
                  </div>

                  {/* Description - Full Width */}
                  <div className={`md:col-span-2 ${formSectionClass}`}>
                    <label htmlFor="description" className={labelClasses}>
                      <FileText
                        className={`w-4 h-4 ${theme.tailwind.text16}`}
                      />
                      <span>Description</span>
                    </label>
                    <Field
                      as="textarea"
                      id="description"
                      name="description"
                      rows="4"
                      className={`${inputClasses} resize-none`}
                      placeholder="Enter additional details about this transaction"
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className={errorClasses}
                    />
                  </div>

                  {/* Submit Button - Full Width */}
                  <div className="md:col-span-2 flex justify-center mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full md:w-1/2 py-4 px-6 ${
                        theme.tailwind.bgr12
                      } text-white rounded-xl shadow-lg ${
                        theme.tailwind.shadowing1
                      } transform transition-all duration-300 hover:scale-105 ${
                        theme.tailwind.shadowhover4
                      } font-medium text-lg flex items-center justify-center ${
                        isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center space-x-2">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        <span>Create Transaction</span>
                      )}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default TransactionCreate;
